import { useMemo, useState } from 'react';
import { loanCapitalProgram, loanLtvLtc, loanMaturity, ltvLtcText, maturityBasisNote } from '@/pages/pipeline-intelligence/dealResolution'
import {
  Shield,
  AlertTriangle,
  TrendingUp,
  Eye,
  ArrowUpRight,
  ArrowDownRight,
  CheckCircle2,
  Clock,
  FileText,
  CalendarClock,
  HardHat,
  Bell,
  Activity,
  Newspaper,
  Minus,
} from 'lucide-react';

import { ANTARCTICA_DEALS } from '@/data/clientMockData'
import { useModuleLoans } from '@/hooks/useDemoDataAccess'
import ConstructionMonitoring from '@/components/surveillance/ConstructionMonitoring'
import PostCloseValuationPanel from '@/components/surveillance/PostCloseValuationPanel'
import {
  eventsFromLoanTriggers,
  safeAverage,
  surveillanceBook,
  toMillions,
  amountUnitOf,
  watchlistFromLoans,
  type CovenantStatus,
  type OverallStatus,
  type SurveillanceEvent,
  type WatchlistRow,
} from '@/pages/liveModuleData'

// ---------------------------------------------------------------------------
// Funded deals + mock current metrics
// ---------------------------------------------------------------------------

const demoFundedDeals = ANTARCTICA_DEALS.filter((d) => d.stage === 'Funded');

// ---------------------------------------------------------------------------
// Additional mock surveillance events
// ---------------------------------------------------------------------------

const triggerEvents: SurveillanceEvent[] = eventsFromLoanTriggers(demoFundedDeals);

// additional mock events
const additionalEvents: SurveillanceEvent[] = [
  { id: 'EV-001', description: 'Annual financial statements overdue', dealRef: 'ALG-1018', property: 'One Congress Plaza', due: '2026-03-10', severity: 'high', status: 'overdue' },
  { id: 'EV-002', description: 'Insurance renewal — policy expiration approaching', dealRef: 'ALG-1016', property: 'Riverwalk at Tempe Town Lake', due: '2026-04-01', severity: 'medium', status: 'upcoming' },
  { id: 'EV-003', description: 'Tax escrow review — annual reconciliation', dealRef: 'ALG-1017', property: 'Riverwalk at Tempe Town Lake', due: '2026-04-15', severity: 'low', status: 'upcoming' },
  { id: 'EV-004', description: 'Property inspection due — annual site visit', dealRef: 'ALG-1018', property: 'One Congress Plaza', due: '2026-03-28', severity: 'medium', status: 'pending' },
  { id: 'EV-005', description: 'Lease rollover analysis needed — 42K SF vacant', dealRef: 'ALG-1018', property: 'One Congress Plaza', due: '2026-04-30', severity: 'high', status: 'pending' },
];

const allEvents = [...triggerEvents, ...additionalEvents].sort((a, b) => {
  const sevOrder = { high: 0, medium: 1, low: 2 };
  if (sevOrder[a.severity] !== sevOrder[b.severity]) return sevOrder[a.severity] - sevOrder[b.severity];
  return new Date(a.due).getTime() - new Date(b.due).getTime();
});

// ---------------------------------------------------------------------------
// Borrower reporting schedule
// ---------------------------------------------------------------------------

interface ReportingRow {
  property: string;
  reportType: string;
  frequency: string;
  nextDue: string;
  status: 'Received' | 'Pending' | 'Overdue';
}

const reportingSchedule: ReportingRow[] = [
  { property: 'Riverwalk at Tempe Town Lake', reportType: 'Annual Financials', frequency: 'Annual', nextDue: '2026-03-31', status: 'Pending' },
  { property: 'Riverwalk at Tempe Town Lake', reportType: 'Quarterly Rent Roll', frequency: 'Quarterly', nextDue: '2026-04-15', status: 'Pending' },
  { property: 'Riverwalk at Tempe Town Lake', reportType: 'Insurance Certificate', frequency: 'Annual', nextDue: '2026-04-01', status: 'Received' },
  { property: 'Riverwalk at Tempe Town Lake', reportType: 'Monthly P&L', frequency: 'Monthly', nextDue: '2026-04-05', status: 'Pending' },
  { property: 'One Congress Plaza', reportType: 'Annual Financials', frequency: 'Annual', nextDue: '2026-03-10', status: 'Overdue' },
  { property: 'One Congress Plaza', reportType: 'Quarterly Rent Roll', frequency: 'Quarterly', nextDue: '2026-03-31', status: 'Pending' },
  { property: 'One Congress Plaza', reportType: 'Monthly P&L', frequency: 'Monthly', nextDue: '2026-03-15', status: 'Overdue' },
  { property: 'One Congress Plaza', reportType: 'Insurance Certificate', frequency: 'Annual', nextDue: '2026-06-01', status: 'Received' },
  { property: 'Riverwalk at Tempe Town Lake', reportType: 'Tax Escrow Statement', frequency: 'Annual', nextDue: '2026-04-15', status: 'Pending' },
  { property: 'One Congress Plaza', reportType: 'Property Inspection', frequency: 'Annual', nextDue: '2026-03-28', status: 'Pending' },
];

// ---------------------------------------------------------------------------
// Metrics
// ---------------------------------------------------------------------------

const DEMO_WATCHLIST = watchlistFromLoans(demoFundedDeals, { demoDrift: true })

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const fmtDate = (d: string) => {
  const dt = new Date(d + 'T00:00:00');
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const sevColor = (s: 'high' | 'medium' | 'low') =>
  s === 'high' ? 'bg-red-400' : s === 'medium' ? 'bg-amber-400' : 'bg-emerald-400';

const sevBorder = (s: 'high' | 'medium' | 'low') =>
  s === 'high' ? 'border-l-red-400/60' : s === 'medium' ? 'border-l-amber-400/60' : 'border-l-emerald-400/60';

const covenantBadge = (s: CovenantStatus) => {
  if (s === 'Unknown') {
    return <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border text-stone-400 bg-stone-400/10 border-stone-400/20">Unknown</span>
  }
  const cls =
    s === 'Compliant'
      ? 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20'
      : s === 'Watch'
        ? 'text-amber-400 bg-amber-400/10 border-amber-400/20'
        : 'text-red-400 bg-red-400/10 border-red-400/20';
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border ${cls}`}>{s}</span>;
};

const statusBadge = (s: OverallStatus) => {
  if (s === 'Unknown') {
    return <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border text-stone-400 bg-stone-400/10 border-stone-400/20">Unknown</span>
  }
  const cls =
    s === 'Performing'
      ? 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20'
      : s === 'Watch'
        ? 'text-amber-400 bg-amber-400/10 border-amber-400/20'
        : 'text-red-400 bg-red-400/10 border-red-400/20';
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border ${cls}`}>{s}</span>;
};

const reportStatusBadge = (s: 'Received' | 'Pending' | 'Overdue') => {
  const cls =
    s === 'Received'
      ? 'text-emerald-400'
      : s === 'Pending'
        ? 'text-amber-400'
        : 'text-red-400';
  return <span className={`text-[11px] font-medium ${cls}`}>{s}</span>;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type SurvTab = 'watchlist' | 'ticklers' | 'rent-rolls' | 'valuation' | 'construction'

// ---------------------------------------------------------------------------
// Mock risk ticklers (Item 11)
// ---------------------------------------------------------------------------

interface RiskTickler {
  id: string
  property: string
  dealRef: string
  type: string
  dueDate: string
  frequency: string
  severity: 'high' | 'medium' | 'low'
  status: 'overdue' | 'pending' | 'upcoming' | 'resolved'
  dscrCushion?: number    // DSCR headroom above covenant floor
  ltvHeadroom?: number    // LTV headroom below covenant ceiling
  description: string
}

const RISK_TICKLERS: RiskTickler[] = [
  { id: 'TKL-001', property: 'One Congress Plaza', dealRef: 'ALG-1018', type: 'Annual Financials', dueDate: '2026-03-10', frequency: 'Annual', severity: 'high', status: 'overdue', dscrCushion: -0.12, ltvHeadroom: -0.04, description: 'Annual financial statements overdue — borrower has not responded to 3 requests' },
  { id: 'TKL-002', property: 'One Congress Plaza', dealRef: 'ALG-1018', type: 'Covenant Test — DSCR', dueDate: '2026-03-31', frequency: 'Quarterly', severity: 'high', status: 'overdue', dscrCushion: -0.12, ltvHeadroom: -0.04, description: 'DSCR of 1.08x breaches 1.20x covenant floor — cure period expires 2026-04-15' },
  { id: 'TKL-003', property: 'One Congress Plaza', dealRef: 'ALG-1018', type: 'Headline Risk Monitor', dueDate: '2026-04-01', frequency: 'Monthly', severity: 'high', status: 'pending', description: 'Tenant WeWork (22K SF) filed Ch. 11 — lease rejection risk being evaluated' },
  { id: 'TKL-004', property: 'Riverwalk at Tempe Town Lake', dealRef: 'ALG-1017', type: 'Insurance Renewal', dueDate: '2026-04-01', frequency: 'Annual', severity: 'medium', status: 'upcoming', dscrCushion: -0.06, ltvHeadroom: 0.13, description: 'Policy expiration approaching — confirm renewal and updated certificate of insurance' },
  { id: 'TKL-005', property: 'Riverwalk at Tempe Town Lake', dealRef: 'ALG-1017', type: 'Tax Escrow Review', dueDate: '2026-04-15', frequency: 'Annual', severity: 'low', status: 'upcoming', description: 'Annual tax escrow reconciliation — current escrow balance $142K' },
  { id: 'TKL-006', property: 'Riverwalk at Tempe Town Lake', dealRef: 'ALG-1017', type: 'Rent Roll', dueDate: '2026-04-15', frequency: 'Quarterly', severity: 'medium', status: 'pending', dscrCushion: -0.06, ltvHeadroom: 0.13, description: 'Q1 2026 rent roll due — prior quarter showed 87% economic occupancy vs 91% UW' },
  { id: 'TKL-007', property: 'Scottsdale Fashion Square', dealRef: 'ALG-1015', type: 'LTV Valuation Review', dueDate: '2026-06-01', frequency: 'Annual', severity: 'low', status: 'upcoming', dscrCushion: 0.22, ltvHeadroom: 0.12, description: 'Annual post-close valuation required per loan agreement — engage appraiser by May 15' },
]

// Mock rent roll data (Item 11)
interface RentRollSummary {
  property: string
  dealRef: string
  period: string
  totalUnits: number
  occupied: number
  physicalOcc: number
  economicOcc: number
  avgRent: number
  totalRevenue: number
  noi: number
  dscrActual: number
  dscrUW: number
  noiVariance: number
  status: 'In Line' | 'Slight Miss' | 'Material Miss'
}

const RENT_ROLLS: RentRollSummary[] = [
  { property: 'Riverwalk at Tempe Town Lake', dealRef: 'ALG-1017', period: 'Q4 2025', totalUnits: 310, occupied: 270, physicalOcc: 0.871, economicOcc: 0.854, avgRent: 1842, totalRevenue: 498000, noi: 323700, dscrActual: 1.14, dscrUW: 1.28, noiVariance: -0.089, status: 'Slight Miss' },
  { property: 'One Congress Plaza', dealRef: 'ALG-1018', period: 'Q4 2025', totalUnits: 0, occupied: 0, physicalOcc: 0.831, economicOcc: 0.812, avgRent: 0, totalRevenue: 0, noi: 2100000, dscrActual: 1.08, dscrUW: 1.35, noiVariance: -0.201, status: 'Material Miss' },
  { property: 'Scottsdale Fashion Square', dealRef: 'ALG-1015', period: 'Q4 2025', totalUnits: 0, occupied: 0, physicalOcc: 0.953, economicOcc: 0.941, avgRent: 0, totalRevenue: 0, noi: 3820000, dscrActual: 1.44, dscrUW: 1.38, noiVariance: 0.035, status: 'In Line' },
]

export default function Surveillance() {
  const { loans, isLoading, isLive } = useModuleLoans('surveillance', ANTARCTICA_DEALS)
  const [resolvedEvents, setResolvedEvents] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<SurvTab>('watchlist');
  const [resolvedTicklers, setResolvedTicklers] = useState<Set<string>>(new Set());

  const bookDeals = useMemo(
    () => (isLive ? surveillanceBook(loans) : demoFundedDeals),
    [isLive, loans],
  )
  const watchlistData: WatchlistRow[] = useMemo(
    () => (isLive ? watchlistFromLoans(bookDeals) : DEMO_WATCHLIST),
    [isLive, bookDeals],
  )
  const liveEvents = useMemo(() => eventsFromLoanTriggers(bookDeals), [bookDeals])
  const allVisibleSource = isLive ? liveEvents : allEvents
  const visibleEvents = allVisibleSource.filter((e) => !resolvedEvents.has(e.id));
  const ticklers = isLive ? [] : RISK_TICKLERS
  const rentRolls = isLive ? [] : RENT_ROLLS
  const schedule = isLive ? [] : reportingSchedule

  const compliantCount = watchlistData.filter((r) => r.covenantStatus === 'Compliant').length;
  // Health is the share of GRADED covenants that comply. With no covenant
  // test on record every row is Unknown, and the honest answer is "—", not 0%
  // or a grade against thresholds that are not the loan's (MOD-24).
  const gradedCount = watchlistData.filter((r) => r.covenantStatus !== 'Unknown').length;
  const portfolioHealthPct = gradedCount ? Math.round((compliantCount / gradedCount) * 100) : null;
  const activeTriggerCount = allVisibleSource.length;
  const highCount = allVisibleSource.filter((e) => e.severity === 'high').length;
  const mediumCount = allVisibleSource.filter((e) => e.severity === 'medium').length;
  const lowCount = allVisibleSource.filter((e) => e.severity === 'low').length;
  const currentDscrs = watchlistData.map((r) => r.currentDscr).filter((n): n is number => n != null);
  const origDscrs = watchlistData
    .map((r) => r.deal.dscr)
    .filter((n): n is number => typeof n === 'number' && Number.isFinite(n));
  // Current DSCR when a post-close figure exists; otherwise the origination
  // figure, labelled as such.
  const dscrBasis: 'current' | 'orig' | null = currentDscrs.length ? 'current' : origDscrs.length ? 'orig' : null;
  const avgDscr = dscrBasis
    ? Number(safeAverage(dscrBasis === 'current' ? currentDscrs : origDscrs).toFixed(2))
    : null;
  const watchCount = watchlistData.filter((r) => r.overallStatus !== 'Performing' && r.overallStatus !== 'Unknown').length;

  if (isLoading) {
    return <div className="p-4 text-stone-400 text-sm">Loading surveillance book…</div>
  }

  const handleResolve = (id: string) => {
    setResolvedEvents((prev) => new Set(prev).add(id));
  };

  return (
    <div className="page-fill gap-2 px-3 py-2">
      {/* ── Tab Bar ── */}
      <div className="flex flex-shrink-0 items-center gap-1 border-b border-amber-400/10">
        {([
          { id: 'watchlist' as const, label: 'Watchlist & Events', icon: <Eye size={12} /> },
          { id: 'ticklers' as const, label: 'Risk Ticklers', icon: <CalendarClock size={12} /> },
          { id: 'rent-rolls' as const, label: 'Rent Rolls & Reporting', icon: <FileText size={12} /> },
          { id: 'valuation' as const, label: 'Post-Close Valuation', icon: <TrendingUp size={12} /> },
          { id: 'construction' as const, label: 'Construction Monitoring', icon: <HardHat size={12} /> },
        ]).map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-2 text-[11px] font-medium border-b-2 transition-all ${
              activeTab === tab.id
                ? 'border-amber-400 text-amber-300'
                : 'border-transparent text-stone-500 hover:text-stone-300'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {activeTab === 'valuation' && (
        <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin">
          <PostCloseValuationPanel deals={bookDeals} live={isLive} />
        </div>
      )}

      {activeTab === 'construction' && (
        <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin">
          <ConstructionMonitoring live={isLive} />
        </div>
      )}

      {/* ── Risk Ticklers Tab (Item 11) ── */}
      {activeTab === 'ticklers' && (
        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
          {/* Summary metrics */}
          <div className="grid grid-cols-4 gap-3">
            {[
              { label: 'Total Open Ticklers', value: ticklers.filter(t => !resolvedTicklers.has(t.id)).length, color: 'text-stone-200', bg: 'bg-stone-400/10', icon: <Bell size={14} className="text-stone-400" /> },
              { label: 'Overdue', value: ticklers.filter(t => t.status === 'overdue' && !resolvedTicklers.has(t.id)).length, color: 'text-red-400', bg: 'bg-red-400/10', icon: <AlertTriangle size={14} className="text-red-400" /> },
              { label: 'DSCR Cushion Alerts', value: ticklers.filter(t => (t.dscrCushion ?? 0) < 0 && !resolvedTicklers.has(t.id)).length, color: 'text-red-400', bg: 'bg-red-400/10', icon: <Activity size={14} className="text-red-400" /> },
              { label: 'Headline Risk Flags', value: ticklers.filter(t => t.type === 'Headline Risk Monitor').length, color: 'text-amber-400', bg: 'bg-amber-400/10', icon: <Newspaper size={14} className="text-amber-400" /> },
            ].map((m) => (
              <div key={m.label} className={`metric-cell flex items-center gap-3`}>
                <div className={`p-1.5 rounded ${m.bg}`}>{m.icon}</div>
                <div>
                  <div className="metric-cell-label">{m.label}</div>
                  <div className={`metric-cell-value ${m.color}`}>{m.value}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Tickler list */}
          <div className="panel flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="panel-header">
              <div className="flex items-center gap-1.5">
                <CalendarClock size={13} className="text-amber-400/60" />
                <span className="panel-header-title">Risk Ticklers</span>
              </div>
              <span className="text-[10px] text-stone-600">DSCR/LTV headroom · covenant tests · reporting · headline risk</span>
            </div>
            <div className="min-h-0 flex-1 overflow-auto scrollbar-thin">
              <table className="dense-table">
                <thead>
                  <tr>
                    <th>Property</th>
                    <th>Type</th>
                    <th>Due Date</th>
                    <th>Freq</th>
                    <th className="text-center">DSCR Cushion</th>
                    <th className="text-center">LTV Headroom</th>
                    <th className="text-center">Severity</th>
                    <th className="text-center">Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {ticklers.filter(t => !resolvedTicklers.has(t.id)).length === 0 ? (
                    <tr>
                      <td colSpan={9} className="py-8 text-center text-stone-500 text-[11px]">
                        {isLive ? 'No risk ticklers are on file for the live book.' : 'No open ticklers.'}
                      </td>
                    </tr>
                  ) : null}
                  {ticklers.filter(t => !resolvedTicklers.has(t.id)).map((t) => (
                    <tr key={t.id} className={t.status === 'overdue' ? 'bg-red-400/[0.03]' : ''}>
                      <td className="whitespace-nowrap">
                        <div className="font-medium text-stone-200">{t.property}</div>
                        <div className="text-[9px] text-stone-600 font-mono">{t.dealRef}</div>
                      </td>
                      <td className="text-[11px]">{t.type}</td>
                      <td className={`font-mono text-[11px] ${t.status === 'overdue' ? 'text-red-400' : 'text-stone-400'}`}>
                        {t.dueDate}
                      </td>
                      <td className="text-[10px] text-stone-500">{t.frequency}</td>
                      <td className="text-center">
                        {t.dscrCushion != null ? (
                          <span className={`font-mono text-[11px] ${t.dscrCushion < 0 ? 'text-red-400 font-bold' : t.dscrCushion < 0.10 ? 'text-amber-400' : 'text-emerald-400'}`}>
                            {t.dscrCushion >= 0 ? '+' : ''}{t.dscrCushion.toFixed(2)}x
                          </span>
                        ) : <Minus size={9} className="mx-auto text-stone-700" />}
                      </td>
                      <td className="text-center">
                        {t.ltvHeadroom != null ? (
                          <span className={`font-mono text-[11px] ${t.ltvHeadroom < 0 ? 'text-red-400 font-bold' : t.ltvHeadroom < 0.05 ? 'text-amber-400' : 'text-emerald-400'}`}>
                            {t.ltvHeadroom >= 0 ? '+' : ''}{(t.ltvHeadroom * 100).toFixed(1)}%
                          </span>
                        ) : <Minus size={9} className="mx-auto text-stone-700" />}
                      </td>
                      <td className="text-center">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${
                          t.severity === 'high' ? 'text-red-400 bg-red-400/10 border-red-400/20' :
                          t.severity === 'medium' ? 'text-amber-400 bg-amber-400/10 border-amber-400/20' :
                          'text-emerald-400 bg-emerald-400/10 border-emerald-400/20'
                        }`}>
                          {t.severity}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${
                          t.status === 'overdue' ? 'text-red-400 bg-red-400/10 border-red-400/20' :
                          t.status === 'pending' ? 'text-amber-400 bg-amber-400/10 border-amber-400/20' :
                          'text-stone-400 bg-stone-400/10 border-stone-400/20'
                        }`}>
                          {t.status}
                        </span>
                      </td>
                      <td>
                        <button
                          onClick={() => setResolvedTicklers(prev => new Set(prev).add(t.id))}
                          className="text-[10px] text-emerald-400 hover:text-emerald-300 px-2 py-0.5 rounded border border-emerald-400/20 hover:bg-emerald-400/10 transition-colors"
                        >
                          Resolve
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Rent Rolls & Reporting Tab (Item 11) ── */}
      {activeTab === 'rent-rolls' && (
        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
          <div className="panel flex min-h-0 flex-[1.2] flex-col overflow-hidden">
            <div className="panel-header">
              <div className="flex items-center gap-1.5">
                <FileText size={13} className="text-amber-400/60" />
                <span className="panel-header-title">Periodic Reporting Summary</span>
              </div>
              <span className="text-[10px] text-stone-600">quarterly/monthly financials, rent rolls, variance vs underwriting</span>
            </div>
            <div className="min-h-0 flex-1 overflow-auto scrollbar-thin">
              <table className="dense-table">
                <thead>
                  <tr>
                    <th>Property</th>
                    <th>Period</th>
                    <th className="text-right">Phys. Occ</th>
                    <th className="text-right">Econ. Occ</th>
                    <th className="text-right">NOI ($)</th>
                    <th className="text-right">DSCR (Actual)</th>
                    <th className="text-right">DSCR (UW)</th>
                    <th className="text-right">NOI Variance</th>
                    <th className="text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rentRolls.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="py-8 text-center text-stone-500 text-[11px]">
                        {isLive ? 'No periodic reporting has been received for live loans.' : 'No rent rolls on file.'}
                      </td>
                    </tr>
                  ) : null}
                  {rentRolls.map((r, i) => (
                    <tr key={i}>
                      <td>
                        <div className="font-medium text-stone-200">{r.property}</div>
                        <div className="text-[9px] text-stone-600 font-mono">{r.dealRef}</div>
                      </td>
                      <td className="text-stone-400">{r.period}</td>
                      <td className="text-right font-mono">{(r.physicalOcc * 100).toFixed(1)}%</td>
                      <td className={`text-right font-mono ${r.economicOcc < 0.85 ? 'text-amber-400' : 'text-stone-300'}`}>
                        {(r.economicOcc * 100).toFixed(1)}%
                      </td>
                      <td className="text-right font-mono text-stone-300">
                        ${(r.noi / 1_000_000).toFixed(2)}M
                      </td>
                      <td className={`text-right font-mono font-semibold ${r.dscrActual < 1.20 ? 'text-red-400' : r.dscrActual < 1.30 ? 'text-amber-400' : 'text-emerald-400'}`}>
                        {r.dscrActual.toFixed(2)}x
                      </td>
                      <td className="text-right font-mono text-stone-500">{r.dscrUW.toFixed(2)}x</td>
                      <td className={`text-right font-mono ${r.noiVariance < -0.10 ? 'text-red-400' : r.noiVariance < 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
                        {r.noiVariance >= 0 ? '+' : ''}{(r.noiVariance * 100).toFixed(1)}%
                      </td>
                      <td className="text-center">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${
                          r.status === 'In Line' ? 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20' :
                          r.status === 'Slight Miss' ? 'text-amber-400 bg-amber-400/10 border-amber-400/20' :
                          'text-red-400 bg-red-400/10 border-red-400/20'
                        }`}>
                          {r.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {/* Reporting Schedule */}
          <div className="panel flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="panel-header">
              <span className="panel-header-title">Borrower Reporting Schedule</span>
            </div>
            <div className="min-h-0 flex-1 overflow-auto scrollbar-thin">
              <table className="dense-table">
                <thead>
                  <tr>
                    <th>Property</th>
                    <th>Report Type</th>
                    <th>Frequency</th>
                    <th>Next Due</th>
                    <th className="text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {schedule.map((r, i) => (
                    <tr key={i}>
                      <td className="text-stone-300">{r.property}</td>
                      <td className="text-stone-400">{r.reportType}</td>
                      <td className="text-stone-500 text-[10px]">{r.frequency}</td>
                      <td className={`font-mono text-[11px] ${r.status === 'Overdue' ? 'text-red-400' : 'text-stone-400'}`}>{r.nextDue}</td>
                      <td className="text-center">{reportStatusBadge(r.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'watchlist' && (
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
      {/* ── Metrics Strip ── */}
      <div className="grid flex-shrink-0 grid-cols-4 gap-2">
        <div className="metric-cell flex items-center gap-3">
          <div className="p-1.5 rounded bg-emerald-400/10">
            <Shield size={14} className="text-emerald-400" />
          </div>
          <div>
            <div className="metric-cell-label">Portfolio Health</div>
            <div className="metric-cell-value text-emerald-400">{portfolioHealthPct == null ? '—' : `${portfolioHealthPct}%`}</div>
            {portfolioHealthPct == null && (
              <div className="text-[9px] text-stone-500">no covenant tests recorded</div>
            )}
          </div>
        </div>
        <div className="metric-cell flex items-center gap-3">
          <div className="p-1.5 rounded bg-red-400/10">
            <AlertTriangle size={14} className="text-red-400" />
          </div>
          <div>
            <div className="metric-cell-label">Active Triggers</div>
            <div className="metric-cell-value">
              {activeTriggerCount}
              <span className="text-[9px] font-mono tracking-wide text-stone-500 ml-2">
                {highCount}H / {mediumCount}M / {lowCount}L
              </span>
            </div>
          </div>
        </div>
        <div className="metric-cell flex items-center gap-3">
          <div className="p-1.5 rounded bg-amber-400/10">
            <TrendingUp size={14} className="text-amber-400" />
          </div>
          <div>
            <div className="metric-cell-label">Avg Portfolio DSCR{dscrBasis === 'orig' ? ' (orig)' : ''}</div>
            <div className="metric-cell-value">{avgDscr == null ? '—' : `${avgDscr}x`}</div>
          </div>
        </div>
        <div className="metric-cell flex items-center gap-3">
          <div className="p-1.5 rounded bg-amber-400/10">
            <Eye size={14} className="text-amber-300" />
          </div>
          <div>
            <div className="metric-cell-label">Watchlist Items</div>
            <div className="metric-cell-value text-amber-300">{watchCount}</div>
          </div>
        </div>
      </div>

      {/* ── Watchlist Grid ── */}
      <div className="panel flex min-h-0 flex-[1.1] flex-col overflow-hidden">
        <div className="panel-header">
          <span className="panel-header-title">Watchlist</span>
          <span className="text-[10px] font-mono text-stone-600">{bookDeals.length} positions</span>
        </div>
        <div className="min-h-0 flex-1 overflow-auto scrollbar-thin">
          <table className="dense-table">
            <thead>
              <tr>
                <th>Property</th>
                <th>Market</th>
                <th className="text-right">Amount ($M)</th>
                <th>Product</th>
                <th className="text-right">DSCR (Orig)</th>
                <th className="text-right">DSCR (Curr)</th>
                <th className="text-center">Trend</th>
                <th className="text-right">LTV (Orig)</th>
                <th className="text-right">LTV (Curr)</th>
                <th>Maturity</th>
                <th className="text-center">Covenant</th>
                <th className="text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {watchlistData.length === 0 ? (
                <tr>
                  <td colSpan={12} className="py-8 text-center text-stone-500 text-[11px]">
                    {isLive ? 'No live loans are on the surveillance book.' : 'No funded deals are on the watchlist.'}
                  </td>
                </tr>
              ) : watchlistData.map((row) => (
                <tr key={row.deal.id}>
                  <td className="font-medium text-stone-200 whitespace-nowrap">{row.deal.property}</td>
                  <td className="text-stone-400 whitespace-nowrap">{row.deal.location}</td>
                  <td className="text-right font-mono">${toMillions(row.deal.loanAmount, amountUnitOf(row.deal)).toFixed(1)}</td>
                  <td className="text-stone-400">{loanCapitalProgram(row.deal) ?? '—'}</td>
                  <td className="text-right font-mono">{typeof row.deal.dscr === 'number' && Number.isFinite(row.deal.dscr) ? `${row.deal.dscr.toFixed(2)}x` : '—'}</td>
                  <td className={`text-right font-mono ${row.dscrTrend === 'down' ? 'text-red-400' : row.dscrTrend === 'up' ? 'text-emerald-400' : 'text-stone-400'}`}>
                    {row.currentDscr != null ? `${row.currentDscr.toFixed(2)}x` : '—'}
                  </td>
                  <td className="text-center">
                    {row.dscrTrend === 'up' ? (
                      <ArrowUpRight size={13} className="inline text-emerald-400" />
                    ) : row.dscrTrend === 'down' ? (
                      <ArrowDownRight size={13} className="inline text-red-400" />
                    ) : (
                      <Minus size={13} className="inline text-stone-600" />
                    )}
                  </td>
                  <td className="text-right font-mono">{ltvLtcText(row.deal, 0) ?? '—'}</td>
                  <td className={`text-right font-mono ${row.currentLtv != null && loanLtvLtc(row.deal).value != null && row.currentLtv > (loanLtvLtc(row.deal).value as number) ? 'text-red-400' : 'text-emerald-400'}`}>
                    {row.currentLtv != null ? `${(row.currentLtv * 100).toFixed(0)}%` : '—'}
                  </td>
                  <td className="whitespace-nowrap text-stone-400" title={maturityBasisNote(loanMaturity(row.deal))}>
                    {loanMaturity(row.deal).date ? fmtDate(loanMaturity(row.deal).date as string) : '—'}
                  </td>
                  <td className="text-center">{covenantBadge(row.covenantStatus)}</td>
                  <td className="text-center">{statusBadge(row.overallStatus)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Bottom row: Event Queue + Borrower Reporting ── */}
      <div className="grid min-h-0 flex-1 grid-cols-2 gap-2">
        {/* Event Queue */}
        <div className="panel flex min-h-0 flex-col overflow-hidden">
          <div className="panel-header">
            <span className="panel-header-title">Event Queue</span>
            <span className="text-[10px] font-mono text-stone-600">{visibleEvents.length} active</span>
          </div>
          <div className="panel-body overflow-y-auto flex-1 space-y-2 scrollbar-thin">
            {visibleEvents.map((ev) => (
              <div
                key={ev.id}
                className={`rounded border border-amber-400/10 bg-black/20 p-2.5 border-l-2 ${sevBorder(ev.severity)}`}
              >
                <div className="flex items-start gap-2">
                  <span className={`status-dot mt-1.5 ${sevColor(ev.severity)}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[12px] text-stone-200 leading-snug">{ev.description}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-[10px] font-mono text-stone-500">{ev.dealRef}</span>
                      <span className="text-[10px] text-stone-600">{ev.property}</span>
                      <span className="text-[10px] text-stone-500 ml-auto flex items-center gap-1">
                        <CalendarClock size={10} />
                        {fmtDate(ev.due)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 mt-2 ml-4">
                  <button
                    onClick={() => handleResolve(ev.id)}
                    className="px-2.5 py-0.5 text-[10px] font-medium rounded border border-emerald-400/20 text-emerald-400 bg-emerald-400/5 hover:bg-emerald-400/10 transition-colors"
                  >
                    Resolve
                  </button>
                  <button className="px-2.5 py-0.5 text-[10px] font-medium rounded border border-amber-400/20 text-amber-400 bg-amber-400/5 hover:bg-amber-400/10 transition-colors">
                    Escalate
                  </button>
                </div>
              </div>
            ))}
            {visibleEvents.length === 0 && (
              <div className="flex flex-col items-center justify-center py-8 text-stone-600">
                <CheckCircle2 size={20} className="mb-2" />
                <p className="text-[11px]">All events resolved</p>
              </div>
            )}
          </div>
        </div>

        {/* Borrower Reporting */}
        <div className="panel flex min-h-0 flex-col overflow-hidden">
          <div className="panel-header">
            <span className="panel-header-title">Borrower Reporting</span>
            <span className="text-[10px] font-mono text-stone-600">{schedule.length} items</span>
          </div>
          <div className="overflow-y-auto flex-1 scrollbar-thin">
            <table className="dense-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Report Type</th>
                  <th>Frequency</th>
                  <th>Next Due</th>
                  <th className="text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {schedule.map((row, i) => (
                  <tr key={i}>
                    <td className="whitespace-nowrap text-stone-300 max-w-[180px] truncate">{row.property}</td>
                    <td className="whitespace-nowrap">
                      <span className="flex items-center gap-1.5">
                        <FileText size={10} className="text-stone-600" />
                        {row.reportType}
                      </span>
                    </td>
                    <td className="text-stone-400">{row.frequency}</td>
                    <td className="whitespace-nowrap">
                      <span className="flex items-center gap-1">
                        <Clock size={10} className="text-stone-600" />
                        {fmtDate(row.nextDue)}
                      </span>
                    </td>
                    <td className="text-center">{reportStatusBadge(row.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      </div>
      )}
      </div>
    </div>
  );
}
